/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @version 1.18.0
 * @since 1.18.0
 */

import { ITableColumnUtilProps } from '../../../../components/Table/TableColumnUtil';
import { ColumnFieldType, EColumnCell, EColumnFilter } from '../../../../components/Table/types/columnTypes';
import { TableNameType } from '../../../../components/Table/types/tableTypes';
import {
    API_AUTO_REPORT_ID_ACCOUNT_TABLE,
    API_AUTO_REPORT_ID_ACCOUNT_TABLE_BASKET_EDIT, API_AUTO_REPORT_ID_ACCOUNT_TABLE_EDIT,
    API_AUTO_REPORT_ID_SUBSCRIBER_TABLE, API_AUTO_REPORT_ID_SUBSCRIBER_TABLE_BASKET_EDIT,
    API_AUTO_REPORT_ID_SUBSCRIBER_TABLE_EDIT,
} from '../../../../modules/app/Api';
import {
    ICommonTranslation,
    IPagePersonalAccountTranslation, IPageSubscriberMobileTranslation,
} from '../../../../modules/internationalization/types';
import { IAutoReportIdTableTranslation } from '../../../../modules/internationalization/types/pages/autoReportId';
import { personalAccountColumnNames, PersonalAccountField } from '../../../../types/personalAccountTypes';
import { SubscriberColumnNames, SubscriberMobileField } from '../../../../types/subscriberTypes';
import {
    cellConfig as tableEditAccountCellConfig,
    getColumnCellType as getTableEditAccountColumnCellType,
    getColumnFilterType as getTableEditAccountColumnFilterType,
    getColumnFixedType as getTableEditAccountColumnFixedType,
} from '../../../PagePersonalAccount/components/PersonalAccountTable/Columns/Cells';
import {
    cellConfig as tableEditSubscriberCellConfig,
    getColumnCellType as getTableEditSubscriberColumnCellType,
    getColumnFilterType as getTableEditSubscriberColumnFilterType,
    getColumnFixedType as getTableEditSubscriberColumnFixedType,
} from '../../../PageSubscriberMobile/components/SubscriberMobileTable/Columns/Cells';

/*
* -------------------------------------------
*            Table Field
* -------------------------------------------
*/

export enum EAutoReportIdTableAccountField {
    delete = 'delete',
    name = 'name',      // Клиент
    number = 'number',  // Лицевой счет
}

export enum EAutoReportIdTableSubscriberField {
    delete = 'delete',
    msisdn = 'msisdn',  // Номер
    name = 'name',      // ФИО
}

/*
* -------------------------------------------
*          Table Column Names
* -------------------------------------------
*/

const TableAccountColumnNames: EAutoReportIdTableAccountField[] = [
    EAutoReportIdTableAccountField.name,
    EAutoReportIdTableAccountField.number,
    EAutoReportIdTableAccountField.delete,
];

const TableSubscriberColumnNames: EAutoReportIdTableSubscriberField[] = [
    EAutoReportIdTableSubscriberField.msisdn,
    EAutoReportIdTableSubscriberField.name,
    EAutoReportIdTableSubscriberField.delete,
];

export type TAutoReportIdTableAccountData = { [Field in EAutoReportIdTableAccountField]: object };
export type TAutoReportIdTableSubscriberData = { [Field in EAutoReportIdTableSubscriberField]: object };


/*
* -------------------------------------------
*          Table Cell Config
* -------------------------------------------
*/

const TableAccountCellConfig: { [Field in EAutoReportIdTableAccountField]: number } = {
    [EAutoReportIdTableAccountField.name]: 300,
    [EAutoReportIdTableAccountField.number]: 300,
    [EAutoReportIdTableAccountField.delete]: 32,
};

const TableSubscriberCellConfig: { [Field in EAutoReportIdTableSubscriberField]: number } = {
    [EAutoReportIdTableSubscriberField.msisdn]: 300,
    [EAutoReportIdTableSubscriberField.name]: 300,
    [EAutoReportIdTableSubscriberField.delete]: 32,
};

export const getAutoReportIdTableAccountColumnFilterType = (name: string): string => {
    switch (name) {
        case EAutoReportIdTableAccountField.number:
            return EColumnFilter.filterMultiSelect;
        case EAutoReportIdTableAccountField.name:
            return EColumnFilter.filterSearchInput;
        default:
            return '';
    }
};

export const getAutoReportIdTableSubscriberColumnFilterType = (name: string): string => {
    switch (name) {
        case EAutoReportIdTableSubscriberField.msisdn:
            return EColumnFilter.filterMultiSelect;
        case EAutoReportIdTableSubscriberField.name:
            return EColumnFilter.filterSearchInput;
        default:
            return '';
    }
};

const getTableAccountColumnCellType = (name: EAutoReportIdTableAccountField): string => {
    switch (name) {
        case EAutoReportIdTableAccountField.number:
            return EColumnCell.linkAccount;
        case EAutoReportIdTableAccountField.delete:
            return EColumnCell.rowDeleteAction;
        default:
            return EColumnCell.text;
    }
};

const getTableSubscriberColumnCellType = (name: EAutoReportIdTableSubscriberField): string => {
    switch (name) {
        case EAutoReportIdTableSubscriberField.msisdn:
            return EColumnCell.linkMobile;
        case EAutoReportIdTableSubscriberField.delete:
            return EColumnCell.rowDeleteAction;
        default:
            return EColumnCell.text;
    }
};

/*
* -------------------------------------------
*          Table Column Utils
* -------------------------------------------
*/

export type TTableTranslation =
    (IPagePersonalAccountTranslation | IPageSubscriberMobileTranslation | IAutoReportIdTableTranslation)
    & ICommonTranslation;

type ITableStaticColumnUtil<T extends ColumnFieldType> = Pick<ITableColumnUtilProps<T, TTableTranslation>,
    'cellConfig'
    | 'getColumnCellType'
    | 'getColumnFilterType'
    | 'columnNames'
    | 'getColumnFixedType'>;

interface ITableColumnUtil {
    [TableNameType.autoReportIdAccount]: ITableStaticColumnUtil<EAutoReportIdTableAccountField>;
    [TableNameType.autoReportIdSubscriber]: ITableStaticColumnUtil<EAutoReportIdTableSubscriberField>;
    [TableNameType.autoReportIdAccountEdit]: ITableStaticColumnUtil<PersonalAccountField>;
    [TableNameType.autoReportIdSubscriberEdit]: ITableStaticColumnUtil<SubscriberMobileField>;
}

const TableAccountColumnUtil: ITableStaticColumnUtil<EAutoReportIdTableAccountField> = {
    cellConfig: TableAccountCellConfig,
    getColumnCellType: getTableAccountColumnCellType,
    getColumnFilterType: getAutoReportIdTableAccountColumnFilterType,
    getColumnFixedType: () => false,
    columnNames: TableAccountColumnNames,
};

const TableSubscriberColumnUtil: ITableStaticColumnUtil<EAutoReportIdTableSubscriberField> = {
    cellConfig: TableSubscriberCellConfig,
    getColumnCellType: getTableSubscriberColumnCellType,
    getColumnFilterType: getAutoReportIdTableSubscriberColumnFilterType,
    getColumnFixedType: () => false,
    columnNames: TableSubscriberColumnNames,
};

const TableEditAccountColumnUtil: ITableStaticColumnUtil<PersonalAccountField> = {
    cellConfig: tableEditAccountCellConfig,
    getColumnCellType: getTableEditAccountColumnCellType,
    getColumnFilterType: getTableEditAccountColumnFilterType,
    getColumnFixedType: getTableEditAccountColumnFixedType,
    columnNames: personalAccountColumnNames,
};

const TableEditSubscriberColumnUtil: ITableStaticColumnUtil<SubscriberMobileField> = {
    cellConfig: tableEditSubscriberCellConfig,
    getColumnCellType: getTableEditSubscriberColumnCellType,
    getColumnFilterType: getTableEditSubscriberColumnFilterType,
    getColumnFixedType: getTableEditSubscriberColumnFixedType,
    columnNames: SubscriberColumnNames,
};


export const mapAutoReportIdTableNameToColumnUtil: ITableColumnUtil = {
    [TableNameType.autoReportIdAccount]: TableAccountColumnUtil,
    [TableNameType.autoReportIdSubscriber]: TableSubscriberColumnUtil,
    [TableNameType.autoReportIdAccountEdit]: TableEditAccountColumnUtil,
    [TableNameType.autoReportIdSubscriberEdit]: TableEditSubscriberColumnUtil,
};

/*
* -------------------------------------------
*          Table Api Url
* -------------------------------------------
*/

export const mapAutoReportIdTableApiPath: { [key: string]: string } = {
    [TableNameType.autoReportIdAccount]: API_AUTO_REPORT_ID_ACCOUNT_TABLE,
    [TableNameType.autoReportIdSubscriber]: API_AUTO_REPORT_ID_SUBSCRIBER_TABLE,
    [TableNameType.autoReportIdAccountEdit]: API_AUTO_REPORT_ID_ACCOUNT_TABLE_EDIT,
    [TableNameType.autoReportIdSubscriberEdit]: API_AUTO_REPORT_ID_SUBSCRIBER_TABLE_EDIT,
};

export const mapAutoReportIdTableBasketApiPath: { [key: string]: string | undefined} = {
    [TableNameType.autoReportIdAccount]: undefined,
    [TableNameType.autoReportIdSubscriber]: undefined,
    [TableNameType.autoReportIdAccountEdit]: API_AUTO_REPORT_ID_ACCOUNT_TABLE_BASKET_EDIT,
    [TableNameType.autoReportIdSubscriberEdit]: API_AUTO_REPORT_ID_SUBSCRIBER_TABLE_BASKET_EDIT,
};
